import React, { useMemo } from "react"
import { FaChevronLeft } from "react-icons/fa"
import { useQuery } from "react-query"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { EVENT_CLASS, TEventDetail, TTicket } from "@letshang/constants"
import { get } from "lodash-es"
import { useRecoilValue } from "recoil"

import { userState } from "@fuse/recoil/signup"

import { getAllTicketsApi } from "@api/firebaseWrappedApis"

interface PaymentInsightsProps {
  eventDetails?: TEventDetail | null
}

const PaymentInsights: React.FC<PaymentInsightsProps> = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const eventId = get(params, "eventId", "")

  const eventDetails = get(
    location,
    "state.eventDetails",
    null,
  ) as TEventDetail | null
  const user = useRecoilValue(userState)

  const onBackPress = () => {
    navigate(-1)
  }

  // Check if user is admin or co-host of the event
  const isAdmin = useMemo(() => {
    if (!eventDetails || !user?.uid || !user?.isLoggedIn) return false

    const initiatorUid = get(eventDetails, "eventDocData.initiatorUid", "")
    const coHosts = get(eventDetails, "eventDocData.coHosts", {}) as Record<
      string,
      boolean
    >

    return initiatorUid === user.uid || coHosts[user.uid] === true
  }, [eventDetails, user?.uid, user?.isLoggedIn])

  // Fetch tickets data using the API
  const { data: ticketsData, isLoading: isLoadingTickets } = useQuery(
    ["getAllTickets", eventId],
    () => getAllTicketsApi({ eventId }),
    {
      enabled: !!eventId,
      refetchOnWindowFocus: false,
    },
  )

  // Extract ticket data from API response and eventDetails
  const ticketData = useMemo(() => {
    if (!eventDetails || !ticketsData) return null

    const tickets = get(ticketsData, "data.tickets", []) as TTicket[]

    // Check if this is a ticketed event
    const eventClass = get(eventDetails, "eventDocData.eventClass", "")
    const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

    if (!isTicketedEvent) {
      return {
        totalRevenue: 0,
        totalTicketsSold: 0,
        totalCapacity: 0,
        ticketSummary: [],
        isTicketedEvent: false,
      }
    }

    // Group tickets by ticket name and calculate totals
    const ticketSummary = tickets.reduce(
      (acc, ticket) => {
        const ticketName = get(ticket, "ticketName", "Unknown Ticket")
        const ticketPrice = get(ticket, "ticketPrice", 0)
        const ticketQuantity = get(ticket, "ticketQuantity", 0)
        const totalPurchased = get(ticket, "totalPurchased", 0) // This is the actual number sold

        if (!acc[ticketName]) {
          acc[ticketName] = {
            name: ticketName,
            unitsSold: totalPurchased,
            totalQuantity: ticketQuantity,
            revenue: totalPurchased * ticketPrice,
            price: ticketPrice,
          }
        }

        return acc
      },
      {} as Record<string, any>,
    )

    // Calculate totals
    const totalRevenue = Object.values(ticketSummary).reduce(
      (sum: number, ticket: any) => sum + ticket.revenue,
      0,
    )
    const totalTicketsSold = Object.values(ticketSummary).reduce(
      (sum: number, ticket: any) => sum + ticket.unitsSold,
      0,
    )
    const totalCapacity = Object.values(ticketSummary).reduce(
      (sum: number, ticket: any) => sum + ticket.totalQuantity,
      0,
    )

    return {
      totalRevenue,
      totalTicketsSold,
      totalCapacity,
      ticketSummary: Object.values(ticketSummary),
      isTicketedEvent: true,
    }
  }, [eventDetails, ticketsData])

  // If no event details, show error
  if (!eventDetails) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Payment Insights</h1>
          <p>No event data available</p>
        </div>
      </div>
    )
  }

  // If user is not admin or co-host, show access denied
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="mb-4">
            {!user?.isLoggedIn
              ? "Please log in to view payment insights."
              : "Only event hosts and co-hosts can view payment insights."}
          </p>
          <button
            onClick={onBackPress}
            className="px-6 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const flyerImage = get(eventDetails, "eventDocData.flyerImage", "")

  return (
    <BackgroundWrapper bgImage={flyerImage} className="min-h-screen">
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-screen flex flex-col"
      >
        {/* Header */}
        <div className="fixed top-0 left-0 w-full z-10 bg-white/10 backdrop-blur-lg">
          <div className="flex items-center px-6 py-4">
            <button
              onClick={onBackPress}
              className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <FaChevronLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-bold text-white text-center flex-1">
              Payment Insights
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pt-24 px-6 pb-6 text-left">
          {isLoadingTickets ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Revenue to date section */}
              <div className="bg-white/10 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-white mb-3">
                  Revenue to date
                </h2>
                <div>
                  <div className="text-4xl font-bold text-white mb-3">
                    ${ticketData?.totalRevenue || 0}
                  </div>
                  <div className="text-white/80">
                    {ticketData?.totalTicketsSold || 0}/
                    {ticketData?.totalCapacity || 0} tickets sold
                  </div>
                </div>
              </div>

              {/* Sales by ticket type section */}
              <h2 className="text-lg font-semibold text-white">
                Sales by ticket type
              </h2>
              <div>
                {/* Table headers */}
                <div className="grid grid-cols-3 gap-4 mb-2 font-medium text-white/90 bg-white/10 rounded-xl px-5 py-3">
                  <div>Ticket type</div>
                  <div>Units sold</div>
                  <div>Revenue</div>
                </div>

                {/* Table rows */}
                <div className="space-y-3">
                  {!ticketData?.isTicketedEvent ? (
                    <div className="text-white/60 text-center py-4">
                      This is not a ticketed event
                    </div>
                  ) : ticketData?.ticketSummary &&
                    ticketData.ticketSummary.length > 0 ? (
                    ticketData.ticketSummary.map((ticket, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-3 gap-4 text-white px-5 py-3 border-b border-white/10"
                      >
                        <div>{ticket.name}</div>
                        <div>
                          {ticket.unitsSold}/{ticket.totalQuantity}
                        </div>
                        <div>${ticket.revenue}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-white/60 text-center py-4">
                      No tickets available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default PaymentInsights
